import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
  type UseQueryOptions,
} from '@tanstack/react-query';
import {
  boardMapSchema,
  cardDetailSchema,
  cardLocationPutSchema,
  type ApiError,
  type BoardMap,
  type CardDetail,
  type CardLocation,
  type CardLocationPut,
  type LabelColour,
  type MapPin,
} from '@molt/shared';
import { labelColourDisplayName } from '@/components/palette';
import { del, get, put } from './api';
import {
  EMPTY_BOARD_FILTER,
  boardFilterCacheKey,
  boardFilterSearchParams,
  type BoardFilterSelection,
} from './board-filter';
import { queryKeys } from './query-keys';

export function boardMapQueryString(
  filter: BoardFilterSelection = EMPTY_BOARD_FILTER,
): string {
  return boardFilterSearchParams(filter).toString();
}

export function getBoardMap(
  boardId: string,
  filter: BoardFilterSelection = EMPTY_BOARD_FILTER,
): Promise<BoardMap> {
  const query = boardMapQueryString(filter);
  const suffix = query === '' ? '' : `?${query}`;
  return get(`/boards/${encodeURIComponent(boardId)}/map${suffix}`, boardMapSchema);
}

export function boardMapQueryOptions(
  boardId: string,
  filter: BoardFilterSelection = EMPTY_BOARD_FILTER,
): UseQueryOptions<BoardMap, ApiError> {
  return {
    queryKey: queryKeys.boards.map(boardId, boardFilterCacheKey(filter)),
    queryFn: () => getBoardMap(boardId, filter),
  };
}

export function setCardLocation(
  cardId: string,
  location: CardLocationPut,
): Promise<CardDetail> {
  return put(
    `/cards/${encodeURIComponent(cardId)}/location`,
    cardDetailSchema,
    cardLocationPutSchema.parse(location),
  );
}

export function removeCardLocation(cardId: string): Promise<CardDetail> {
  return del(`/cards/${encodeURIComponent(cardId)}/location`, cardDetailSchema);
}

export function useSetCardLocation(): UseMutationResult<
  CardDetail,
  Error,
  {
    readonly cardId: string;
    readonly boardId: string;
    readonly location: CardLocationPut;
  }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ cardId, location }) => setCardLocation(cardId, location),
    onSuccess: (detail, variables) => {
      queryClient.setQueryData(queryKeys.cards.detail(variables.cardId), detail);
      void queryClient.invalidateQueries({
        queryKey: queryKeys.cards.detail(variables.cardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.map(variables.boardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.contents(variables.boardId),
      });
    },
  });
}

export function useRemoveCardLocation(): UseMutationResult<
  CardDetail,
  Error,
  { readonly cardId: string; readonly boardId: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ cardId }) => removeCardLocation(cardId),
    onSuccess: (detail, variables) => {
      queryClient.setQueryData(queryKeys.cards.detail(variables.cardId), detail);
      void queryClient.invalidateQueries({
        queryKey: queryKeys.cards.detail(variables.cardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.map(variables.boardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.contents(variables.boardId),
      });
    },
  });
}

export function mapPinPosition(location: Pick<CardLocation, 'lat' | 'lng'>): {
  readonly leftPercent: number;
  readonly topPercent: number;
} {
  return {
    leftPercent: Math.min(Math.max(((location.lng + 180) / 360) * 100, 0), 100),
    topPercent: Math.min(Math.max(((90 - location.lat) / 180) * 100, 0), 100),
  };
}

/**
 * FE-80.1 — `t` is positional and first, matching `dueStatusLabel` and
 * `formatCardDate` (`lib/card-dates.ts`), the two other lib leaves that take a
 * translator. One call site (`components/board/BoardMapView.tsx`), so the
 * signature change is a two-file edit and its own test.
 *
 * The `cover_colour` arm resolves its fixed colour vocabulary through
 * `labelColourDisplayName`; stored label names remain literal user data.
 */
export function mapPinAppearanceLabel(
  t: (key: string, values?: Record<string, string | number>, count?: number) => string,
  pin: MapPin,
): string {
  const first = pin.cards[0]?.pinAppearance;
  if (first?.source === 'cover_colour')
    return t('boardMap.pinAppearance.coverColour', {
      colour: labelColourDisplayName(t, first.colour as LabelColour),
    });
  if (first?.source === 'cover_photo') return t('boardMap.pinAppearance.coverPhoto');
  if (first?.source === 'cover_attachment')
    return t('boardMap.pinAppearance.coverAttachment');
  if (first?.source === 'label')
    return first.label.name === null
      ? t('boardMap.pinAppearance.label')
      : t('boardMap.pinAppearance.labelNamed', { name: first.label.name });
  return t('boardMap.pinAppearance.default');
}

export function normaliseMapCoordinateInput(
  value: string | number,
  kind: 'lat' | 'lng',
): number | null {
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) return null;
  const min = kind === 'lat' ? -90 : -180;
  const max = kind === 'lat' ? 90 : 180;
  if (numeric < min || numeric > max) return null;
  const normalised = Number(numeric.toFixed(7));
  return Object.is(normalised, -0) ? 0 : normalised;
}
